import { createApi, emit } from './api';
import { allowlistAllows } from './allowlist';
import { sendToIframe, makeIframeListener } from './postmessage';
import { injectIframe, showIframe, hideIframe } from './iframe';
import { applyLayout, removeLayoutStyle, awaitMount } from './layout';
import type { BootConfig, WidgetConfig } from './types';

/** Default API origin — the public Apptor Flow API. The widget script may
 *  be loaded from any origin (CDN, customer site bundle, tenant subdomain),
 *  so the API host has to be configured explicitly via `data-api`. The
 *  default points at the production API; non-prod environments override it. */
const DEFAULT_API_ORIGIN = 'https://ai-api.apptor.io';

/** Iframe origin — the host that serves the chat SPA at `/w/{slug}`. Same
 *  rule as DEFAULT_API_ORIGIN: configurable per environment, but defaults
 *  to the production tenant-shared host. */
const DEFAULT_IFRAME_ORIGIN = 'https://expeed.flow.apptor.io';

/** Read the API base from the script tag's `data-api` attribute, falling
 *  back to the production default. The host page controls this — there's
 *  no security concern because the API endpoints we call (widget-config,
 *  frame-ancestors) are already public, anonymous, and rate-limited. */
function readScriptAttr(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const tag = (document.currentScript as HTMLScriptElement | null) ?? null;
  if (tag?.dataset?.[name]) return tag.dataset[name];
  const found = document.querySelector(`script[data-${name}][src*="/widget.js"]`) as HTMLScriptElement | null;
  return found?.dataset?.[name];
}

function deriveApiOrigin(): string {
  const explicit = readScriptAttr('api');
  if (explicit) {
    try { return new URL(explicit).origin; } catch { /* fall through */ }
  }
  // Default to the script's own origin so same-origin fetches work without
  // CORS — the editor nginx proxies /api/published-workflows/by-slug/.../
  // widget-config to the real API host. Only customers who load the script
  // from their own CDN need to override via `data-api`, in which case the
  // backend must be CORS-enabled. For everyone using the Studio's Install
  // snippet (script src = tenant host), no CORS work needed.
  if (typeof document !== 'undefined') {
    const tag = document.currentScript as HTMLScriptElement | null;
    if (tag?.src) {
      try { return new URL(tag.src).origin; } catch { /* fall through */ }
    }
  }
  return DEFAULT_API_ORIGIN;
}

function deriveIframeOrigin(): string {
  const explicit = readScriptAttr('iframe');
  if (explicit) {
    try { return new URL(explicit).origin; } catch { /* fall through */ }
  }
  // The script's own origin is a sane fallback if `data-iframe` isn't set —
  // covers the common case where the loader is served from the same host
  // that renders /w/{slug}.
  if (typeof document !== 'undefined') {
    const tag = document.currentScript as HTMLScriptElement | null;
    if (tag?.src) {
      try { return new URL(tag.src).origin; } catch { /* fall through */ }
    }
  }
  return DEFAULT_IFRAME_ORIGIN;
}

const api = createApi();
(window as any).ApptorChat = api;

interface State {
  /** Origin that serves the chat SPA at /w/{slug} — used to construct the
   *  iframe src and to validate postMessage origins. */
  iframeOrigin: string;
  /** Origin that serves the public widget-config + frame-ancestors API.
   *  CORS-enabled so the loader can fetch directly without an editor proxy. */
  apiOrigin: string;
  iframe?: HTMLIFrameElement;
  fab?: HTMLButtonElement;
  config?: WidgetConfig;
  bootConfig?: BootConfig;
  ready: boolean;
  /** I12 — the active iframe-message listener; held so we can remove it on re-boot/destroy. */
  messageListener?: (e: MessageEvent) => void;
  /** Disposer for the awaitMount observer; cleared on destroy / re-boot. */
  disposeMountWatcher?: () => void;
}
const state: State = {
  iframeOrigin: deriveIframeOrigin(),
  apiOrigin: deriveApiOrigin(),
  ready: false
};

/** I13 — flag the rAF poll loop watches; flipped on destroy and back on next boot. */
let rafAlive = true;

function findScriptTag(): HTMLScriptElement | null {
  if (document.currentScript && (document.currentScript as HTMLScriptElement).dataset?.flow) {
    return document.currentScript as HTMLScriptElement;
  }
  return document.querySelector('script[data-flow][src*="/widget.js"]') as HTMLScriptElement | null;
}

async function autoBoot() {
  const tag = findScriptTag();
  if (!tag) return;
  const flow = tag.dataset.flow;
  if (!flow) return;
  // C3 — `data-api-base` is no longer trusted; origin always derived from script URL.
  const launcher = tag.dataset.launcher === 'off'
    ? { mode: 'host' as const, position: 'BR' as const, icon: 'chat' as const, color: '', openOn: 'click' as const }
    : undefined;
  api.boot({ flowSlug: flow, ...(launcher ? { launcher } : {}) });
}

async function applyBoot(boot: BootConfig) {
  state.bootConfig = boot;
  // C3 — `boot.apiBase` is no longer trusted; origin always derived from script URL.

  let res: Response;
  try {
    res = await fetch(`${state.apiOrigin}/api/published-workflows/by-slug/${encodeURIComponent(boot.flowSlug)}/widget-config`);
  } catch {
    console.warn('[ApptorChat] failed to fetch widget config');
    return;
  }
  if (!res.ok) {
    console.warn('[ApptorChat] widget config not found:', res.status);
    return;
  }
  const config: WidgetConfig = await res.json();
  state.config = config;

  if (!allowlistAllows(config.install.domains, window.location.host)) {
    console.warn(
      `[ApptorChat] domain "${window.location.host}" is not in the allowlist for flow "${boot.flowSlug}".`,
      'Configure domains under the Install tab of Widget Studio.'
    );
    return;
  }

  // I11 — only request microphone permission when voice input is enabled.
  const allow = config.behavior?.voiceInput ? 'microphone' : '';
  // Defensive shallow-copy so the inline-fallback branch below doesn't
  // mutate the cached `state.config.layout` object on re-boot.
  const layout = { ...(config.layout ?? { preset: 'floating' as const, mount: '', customCss: '' }) };

  // Iframe creation — for `inline` preset we DEFER until awaitMount resolves
  // so the iframe is appended directly into the customer's mount node. The
  // previous implementation appended to <body> first then re-parented via
  // mountNode.appendChild(); Chrome / Firefox abort the iframe's load when
  // it's reparented mid-stream, leaving the chat blank until close+reopen.
  // Inject-once-into-the-final-parent eliminates the load-abort race.
  const inject = (parent?: HTMLElement) => injectIframe(state.iframeOrigin, boot.flowSlug, {
    position: config.launcher.position,
    allow,
    parent
  });

  if (layout.preset === 'inline') {
    if (state.disposeMountWatcher) state.disposeMountWatcher();
    state.disposeMountWatcher = awaitMount(layout.mount, (mountNode) => {
      if (mountNode) {
        state.iframe = inject(mountNode);
        applyLayout(state.iframe, layout);
      } else {
        console.warn(
          `[ApptorChat] layout.mount "${layout.mount}" did not resolve within 30s; falling back to floating preset.`
        );
        state.iframe = inject(); // body
        applyLayout(state.iframe, { ...layout, preset: 'floating' as const });
      }
    });
  } else {
    state.iframe = inject(); // body
    applyLayout(state.iframe, layout);
  }

  // I12 — remove any prior listener (re-boot scenario) before installing a new one.
  if (state.messageListener) {
    window.removeEventListener('message', state.messageListener);
  }
  state.messageListener = makeIframeListener(state.iframeOrigin, (msg) => {
    if (msg.type === 'apptor:ready') {
      state.ready = true;
      if (state.iframe) sendToIframe(state.iframe, 'apptor:boot', { ...config, ...boot }, state.iframeOrigin);
      emit(api, 'ready');
    } else if (msg.type === 'apptor:unread') emit(api, 'unread', msg.payload);
    else if (msg.type === 'apptor:user-message' || msg.type === 'apptor:ai-message') emit(api, 'message', msg.payload);
    else if (msg.type === 'apptor:open') emit(api, 'open');
    else if (msg.type === 'apptor:close') emit(api, 'close');
    else if (msg.type === 'apptor:entity-click') emit(api, 'entity:click', msg.payload);
  });
  window.addEventListener('message', state.messageListener);

  const launcherMode = boot.launcher?.mode ?? config.launcher.mode;
  if (launcherMode === 'apptor') renderFab(config);
}

function renderFab(config: WidgetConfig) {
  const fab = document.createElement('button');
  fab.setAttribute('aria-label', 'Open chat');
  fab.style.cssText = `
    position: fixed; z-index: 2147483645;
    width: 56px; height: 56px; border-radius: 50%; border: 0;
    background: ${config.launcher.color || config.appearance.primary};
    color: white; font-size: 24px; cursor: pointer;
    box-shadow: 0 12px 30px rgba(0,0,0,0.25);
    display: flex; align-items: center; justify-content: center;
  `;
  const pos = config.launcher.position;
  if (pos === 'BR') { fab.style.bottom = '24px'; fab.style.right = '24px'; }
  else if (pos === 'BL') { fab.style.bottom = '24px'; fab.style.left = '24px'; }
  else if (pos === 'TR') { fab.style.top = '24px'; fab.style.right = '24px'; }
  else { fab.style.top = '24px'; fab.style.left = '24px'; }
  fab.textContent = '💬';
  fab.addEventListener('click', () => api.open());
  document.body.appendChild(fab);
  state.fab = fab;
}

function processQueue() {
  if (api.__cmdQueue.length === 0) return;
  const q = api.__cmdQueue.splice(0);
  q.forEach(({ cmd, args }) => {
    if (cmd === 'boot') {
      // I13 — re-arm the rAF loop in case a previous destroy paused it.
      if (!rafAlive) {
        rafAlive = true;
        requestAnimationFrame(tick);
      }
      applyBoot(args[0] as BootConfig);
    }
    else if (cmd === 'open' && state.iframe) {
      // The host may have removed the mount node (React/Vue re-render); a
      // detached iframe would silently no-op on display flips. Warn loudly so
      // the customer sees the issue in DevTools.
      if (!state.iframe.isConnected) {
        console.warn(
          '[ApptorChat] iframe is detached from the DOM — host page likely removed the mount node. Re-boot or re-mount.'
        );
      }
      showIframe(state.iframe);
    }
    else if (cmd === 'close' && state.iframe) hideIframe(state.iframe);
    else if (cmd === 'toggle' && state.iframe) {
      if (!state.iframe.isConnected) {
        console.warn('[ApptorChat] iframe is detached; toggle is a no-op.');
      }
      state.iframe.style.display = state.iframe.style.display === 'none' ? 'block' : 'none';
    }
    else if (cmd === 'destroy') {
      state.iframe?.remove(); state.fab?.remove();
      removeLayoutStyle();
      // I12 — unregister the postMessage listener so it doesn't accumulate across boots.
      if (state.messageListener) {
        window.removeEventListener('message', state.messageListener);
        state.messageListener = undefined;
      }
      // Cancel any pending mount observer — host may have unmounted before
      // the slot ever appeared, e.g. SPA route change.
      if (state.disposeMountWatcher) {
        state.disposeMountWatcher();
        state.disposeMountWatcher = undefined;
      }
      state.iframe = undefined; state.fab = undefined; state.ready = false;
      // I13 — pause the rAF loop until a future boot re-arms it.
      rafAlive = false;
    }
    else if (cmd === 'update' && state.iframe && state.ready) {
      const partial = args[0] as Partial<BootConfig>;
      if (partial?.theme) sendToIframe(state.iframe, 'apptor:theme.update', partial.theme, state.iframeOrigin);
      if (partial?.context) sendToIframe(state.iframe, 'apptor:context.update', partial.context, state.iframeOrigin);
    }
    else if (cmd === 'reset' && state.iframe && state.ready) {
      // The iframe ends the current conversation via `new_chat`, its post-chat
      // steps run, and a fresh conversation starts on the same socket. The
      // iframe element itself is not torn down.
      sendToIframe(state.iframe, 'apptor:reset', {}, state.iframeOrigin);
    }
  });
}

const tick = () => {
  if (!rafAlive) return;
  processQueue();
  requestAnimationFrame(tick);
};
requestAnimationFrame(tick);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', autoBoot);
} else {
  autoBoot();
}
